import {
  ArrowLeft,
  CheckCircle,
  Coins,
  Shield,
  Sparkles,
  TrendingUp,
  Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenService from '../services/tokenService';
import useToastStore from '../store/useToastStore';

interface TokenPackage {
  id: string;
  tokens: number;
  price: number;
}

const TokenPurchasePage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToastStore();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTokenPackages();
  }, []);

  const fetchTokenPackages = async () => {
    try {
      setIsLoading(true);
      const packages = await tokenService.getTokenPackages();

      // Only get tokens and price from API
      const transformedPackages: TokenPackage[] = packages
        .filter((pkg) => pkg.isActive)
        .map((pkg) => ({
          id: pkg._id,
          tokens: pkg.tokens,
          price: pkg.price
        }));

      setTokenPackages(transformedPackages);
    } catch (error) {
      console.error('Error fetching token packages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Hardcoded UI configuration based on token amount
  const getPackageConfig = (tokens: number, index: number) => {
    const colors = [
      { color: 'blue', gradient: 'from-blue-500 to-blue-600' },
      { color: 'purple', gradient: 'from-purple-500 to-purple-600' },
      { color: 'green', gradient: 'from-green-500 to-emerald-600' }
    ];

    const descriptions = [
      'Great for getting started with AI website modifications',
      'Most popular choice - perfect for regular updates',
      'Best value - ideal for extensive website customization'
    ];

    const colorScheme = colors[index % colors.length];

    return {
      name: `${(tokens / 1000).toFixed(0)}K Tokens`,
      popular: index === 1,
      icon: <Coins className="w-6 h-6" />,
      features: [
        'Full AI access',
        'All features included',
        'No expiry',
        'Instant activation'
      ],
      description: descriptions[index] || descriptions[2],
      color: colorScheme.color,
      gradient: colorScheme.gradient
    };
  };

  const handlePurchase = async (packageId: string) => {
    setSelectedPackage(packageId);
    setIsProcessing(true);

    try {
      const response = await tokenService.purchaseTokens(packageId);

      if (response.paymentUrl) {
        showToast('Redirecting to payment gateway...', 'info');

        // Store purchase ID in localStorage for confirmation page
        localStorage.setItem('currentPurchaseId', response.purchaseId);

        // Redirect to payment URL
        setTimeout(() => {
          window.location.href = response.paymentUrl!;
        }, 1000);
      } else {
        showToast('Token purchase successful! Your balance has been updated.', 'success');

        // Navigate back to workspace after successful purchase
        setTimeout(() => {
          navigate('/agent/workspace');
        }, 1500);
      }

    } catch (error: any) {
      console.error('Error purchasing tokens:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to purchase tokens. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 mb-4 lg:mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Workspace
          </button>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 lg:w-16 lg:h-16 bg-yellow-500 rounded-lg lg:rounded-xl mb-3 lg:mb-4">
              <Coins className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2 lg:mb-3">
              Purchase Tokens
            </h1>
            <p className="text-sm lg:text-base text-gray-600 max-w-md lg:max-w-2xl mx-auto">
              Get tokens to power AI changes to your website
            </p>
          </div>
        </div>



        {/* Token Packages */}
        <div className="mb-8 lg:mb-12">
          <h2 className="text-lg lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-3 text-center">Choose Your Token Amount</h2>
          <p className="text-center text-sm lg:text-base text-gray-600 mb-6 lg:mb-8 max-w-md lg:max-w-2xl mx-auto">
            Select the number of tokens you need. All options include full AI access.
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-8 lg:py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 lg:h-12 lg:w-12 border-b-2 border-purple-600 mx-auto mb-3 lg:mb-4"></div>
                <p className="text-sm lg:text-base text-gray-600">Loading packages...</p>
              </div>
            </div>
          ) : tokenPackages.length === 0 ? (
            <div className="text-center py-8 lg:py-12">
              <p className="text-sm lg:text-base text-gray-600">No token packages available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 max-w-md lg:max-w-6xl mx-auto">
              {tokenPackages.map((pkg, index) => {
                const config = getPackageConfig(pkg.tokens, index);
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-white border-2 transition-all duration-200 ${config.popular
                      ? 'border-purple-300'
                      : 'border-gray-200'
                      }`}
                  >
                    {config.popular && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <span className="bg-purple-500 text-white text-xs font-semibold px-2 py-1">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="p-4 lg:p-6">
                      <div className="text-center mb-4 lg:mb-6">
                        <div className={`inline-flex items-center justify-center w-10 h-10 lg:w-12 lg:h-12 bg-${config.color}-500 rounded-lg lg:rounded-xl mb-3 lg:mb-4`}>
                          <div className="text-white">
                            {config.icon}
                          </div>
                        </div>

                        <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-1 lg:mb-2">{config.name}</h3>
                        <p className="text-xs lg:text-sm text-gray-600 mb-3 lg:mb-4">{config.description}</p>

                        <div className="mb-3 lg:mb-4">
                          <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                            ₹{pkg.price.toLocaleString()}
                          </div>
                        </div>

                        <div className="text-xs lg:text-sm text-gray-600 mb-4 lg:mb-6">
                          <span className="font-semibold text-sm lg:text-lg">{pkg.tokens.toLocaleString()}</span> tokens
                        </div>
                      </div>

                      <div className="space-y-2 lg:space-y-3 mb-4 lg:mb-6">
                        {config.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center">
                            <CheckCircle className="w-3 h-3 lg:w-4 lg:h-4 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-xs lg:text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={isProcessing && selectedPackage === pkg.id}
                        className={`w-full py-2.5 lg:py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 ${config.popular
                          ? 'bg-purple-500 hover:bg-purple-600'
                          : `bg-${config.color}-500 hover:bg-${config.color}-600`
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isProcessing && selectedPackage === pkg.id ? (
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-3 w-3 lg:h-4 lg:w-4 border-b-2 border-white mr-2"></div>
                            Processing...
                          </div>
                        ) : (
                          'Purchase Now'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* How to Use Tokens Section */}
        <div className="mb-8 lg:mb-12">
          <div className="bg-blue-50 border border-blue-200 p-4 lg:p-6 mb-6 lg:mb-8">
            <h2 className="text-lg lg:text-2xl font-bold text-gray-900 mb-2 lg:mb-4 text-center">How to Use Tokens</h2>
            <p className="text-center text-sm lg:text-base text-gray-600 mb-4 lg:mb-6">
              Making changes to your website is simple! Just chat with our AI.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              <div className="bg-white border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center mb-2 lg:mb-3">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-blue-500 rounded flex items-center justify-center mr-2 lg:mr-3">
                    <span className="text-white font-bold text-xs lg:text-sm">1</span>
                  </div>
                  <h3 className="text-sm lg:text-base font-bold text-gray-900">Describe Your Changes</h3>
                </div>
                <p className="text-xs lg:text-sm text-gray-600 mb-2 lg:mb-3">
                  Simply type what you want to change in the chat.
                </p>
                <ul className="space-y-1 text-xs lg:text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-1 lg:mr-2">•</span>
                    <span>"Change the header color to blue"</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-500 mr-1 lg:mr-2">•</span>
                    <span>"Add a new section about our services"</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center mb-2 lg:mb-3">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-green-500 rounded flex items-center justify-center mr-2 lg:mr-3">
                    <span className="text-white font-bold text-xs lg:text-sm">2</span>
                  </div>
                  <h3 className="text-sm lg:text-base font-bold text-gray-900">AI Makes Changes</h3>
                </div>
                <p className="text-xs lg:text-sm text-gray-600 mb-2 lg:mb-3">
                  Our AI understands your request and modifies your website.
                </p>
                <ul className="space-y-1 text-xs lg:text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-1 lg:mr-2">✓</span>
                    <span>Analyzes what needs to be changed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-1 lg:mr-2">✓</span>
                    <span>Modifies design, content, or layout</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 p-3 lg:p-4">
                <div className="flex items-center mb-2 lg:mb-3">
                  <div className="w-6 h-6 lg:w-8 lg:h-8 bg-purple-500 rounded flex items-center justify-center mr-2 lg:mr-3">
                    <span className="text-white font-bold text-xs lg:text-sm">3</span>
                  </div>
                  <h3 className="text-sm lg:text-base font-bold text-gray-900">Preview & Publish</h3>
                </div>
                <p className="text-xs lg:text-sm text-gray-600 mb-2 lg:mb-3">
                  See the changes instantly and publish when you're happy.
                </p>
                <ul className="space-y-1 text-xs lg:text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-1 lg:mr-2">→</span>
                    <span>View changes in real-time preview</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-1 lg:mr-2">→</span>
                    <span>Publish to make changes live</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
            <div className="bg-white border border-gray-200 p-3 lg:p-4">
              <div className="flex items-center mb-2 lg:mb-3">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-blue-100 rounded flex items-center justify-center mr-2 lg:mr-3">
                  <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                </div>
                <h3 className="text-sm lg:text-base font-semibold text-gray-900">AI-Powered Changes</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-600">Advanced AI makes intelligent modifications to your website</p>
            </div>

            <div className="bg-white border border-gray-200 p-3 lg:p-4">
              <div className="flex items-center mb-2 lg:mb-3">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-green-100 rounded flex items-center justify-center mr-2 lg:mr-3">
                  <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                </div>
                <h3 className="text-sm lg:text-base font-semibold text-gray-900">Real-time Updates</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-600">See your website changes instantly in the live preview</p>
            </div>

            <div className="bg-white border border-gray-200 p-3 lg:p-4">
              <div className="flex items-center mb-2 lg:mb-3">
                <div className="w-8 h-8 lg:w-10 lg:h-10 bg-purple-100 rounded flex items-center justify-center mr-2 lg:mr-3">
                  <Shield className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                </div>
                <h3 className="text-sm lg:text-base font-semibold text-gray-900">Flexible Usage</h3>
              </div>
              <p className="text-xs lg:text-sm text-gray-600">Use tokens for any website modification</p>
            </div>
          </div>
        </div>


        {/* FAQ Section */}
        <div className="bg-white border border-gray-200 p-4 lg:p-6">
          <h2 className="text-lg lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6 text-center">Frequently Asked Questions</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <div>
              <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1 lg:mb-2">How are tokens used for website changes?</h3>
              <p className="text-xs lg:text-sm text-gray-600">Each time you request an AI modification to your website, it consumes tokens. Simple changes use fewer tokens, while complex modifications use more.</p>
            </div>

            <div>
              <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1 lg:mb-2">Do tokens expire?</h3>
              <p className="text-xs lg:text-sm text-gray-600">Tokens don't expire! Once purchased, you can use them anytime to make changes to your website.</p>
            </div>

            <div>
              <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1 lg:mb-2">How many changes can I make?</h3>
              <p className="text-xs lg:text-sm text-gray-600">It depends on the complexity of changes. A 500K token package can handle hundreds of simple changes or dozens of major redesigns.</p>
            </div>

            <div>
              <h3 className="text-sm lg:text-base font-semibold text-gray-900 mb-1 lg:mb-2">What payment methods do you accept?</h3>
              <p className="text-xs lg:text-sm text-gray-600">We accept all major credit cards, UPI, net banking, and digital wallets for secure payments.</p>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-6 lg:mt-8 text-center">
          <div className="flex items-center justify-center space-x-4 lg:space-x-8 text-gray-500">
            <div className="flex items-center">
              <Shield className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              <span className="text-xs lg:text-sm">SSL Secured</span>
            </div>
            <div className="flex items-center">
              <Users className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              <span className="text-xs lg:text-sm">10,000+ Users</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              <span className="text-xs lg:text-sm">24/7 Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenPurchasePage;
